import { PLATFORMS } from '@lib/utils/platforms';
import { formatBytesToString, formatDurationToString } from '@lib/utils/units';
import type { PostContentSummary, SocialPlatformModule } from '../../types';
import { FacebookComposeFields } from './FacebookComposeFields';

/** A reel (the Page's Reels, the distribution of a short) or a video post on the Page (any frame, long-form). */
export type FacebookPostKind = 'reel' | 'video';

/** How each kind reads in the form and the row meta. */
export const FACEBOOK_KIND_LABELS: Record<FacebookPostKind, string> = {
  reel: 'Reel',
  video: 'Video',
};

/** Facebook's caps, in UTF-16 code units, which is what `String.length` counts. */
export const FACEBOOK_TITLE_MAX_LENGTH = 255;
export const FACEBOOK_DESCRIPTION_MAX_LENGTH = 5000;

/**
 * Facebook's exact post content, as the list returns it and edit takes it
 * back. `kind` picks the endpoint and the media rules. No privacy (a Page
 * post is public) and no comment toggle (the API has none). `draft` keeps the
 * post unpublished on the Page, to finish from its tools; the other fields
 * still apply, unlike TikTok's inbox. `aiGenerated` is Meta's self-claim on a
 * reel only: the video endpoint has no such field, so the API refuses it there.
 */
export type FacebookPostContent =
  | {
      platform: 'facebook';
      kind: 'reel';
      title?: string;
      description?: string;
      aiGenerated: boolean;
      draft: boolean;
    }
  | {
      platform: 'facebook';
      kind: 'video';
      title?: string;
      description?: string;
      draft: boolean;
    };

/**
 * The platform's limits per kind, with the Page's name as it is now (a
 * renamed Page shows its new name here). Facebook has no per-account limit to
 * read. `minShortSide` and `minLongSide` are pixels on the frame's two sides.
 */
export type FacebookAccountCapabilities = {
  platform: 'facebook';
  available: true;
  displayName: string;
  limits: {
    reel: {
      minDurationSeconds: number;
      maxDurationSeconds: number;
      minShortSide: number;
      minLongSide: number;
    };
    video: { minDurationSeconds: number; maxDurationSeconds: number; maxSizeBytes: number };
  };
};

const describeContent = (content: FacebookPostContent): PostContentSummary => {
  const caption = content.description || content.title || 'Untitled post';
  return {
    headline: content.draft ? `Draft: ${caption}` : caption,
    privacy: null,
    draft: content.draft,
    variant: FACEBOOK_KIND_LABELS[content.kind],
  };
};

/** The stored shape before this client reads it; `kind` was optional before the API filled its default at preflight. */
type FacebookPostContentWire = {
  platform?: string;
  kind?: string;
  title?: string;
  description?: string;
  aiGenerated?: boolean;
  draft?: boolean;
};

const parseContent = (raw: unknown): FacebookPostContent => {
  const content = raw as FacebookPostContentWire | null;
  if (content?.platform !== 'facebook') {
    throw new Error(`facebook post with content for ${String(content?.platform)}`);
  }
  const base = {
    platform: 'facebook' as const,
    ...(content.title !== undefined && { title: content.title }),
    ...(content.description !== undefined && { description: content.description }),
    draft: content.draft === true,
  };
  // A video row stored before the schema forbade `aiGenerated` on a video
  // carries `false`; sending it back on edit would be refused, so it is not read.
  return content.kind === 'reel'
    ? { ...base, kind: 'reel', aiGenerated: content.aiGenerated === true }
    : { ...base, kind: 'video' };
};

/** A reel, published, with nothing to choose before scheduling: a Page post has no privacy and needs no caption. */
const defaultContent = (): FacebookPostContent => ({
  platform: 'facebook',
  kind: 'reel',
  aiGenerated: false,
  draft: false,
});

const missingField = (content: FacebookPostContent): string | null => {
  if ((content.title ?? '').length > FACEBOOK_TITLE_MAX_LENGTH) {
    return `The title is over ${FACEBOOK_TITLE_MAX_LENGTH.toLocaleString()} characters.`;
  }
  if ((content.description ?? '').length > FACEBOOK_DESCRIPTION_MAX_LENGTH) {
    return `The caption is over ${FACEBOOK_DESCRIPTION_MAX_LENGTH.toLocaleString()} characters.`;
  }
  return null;
};

/** Fields drift by lifecycle stage on backend objects, hence the optionals. */
type FacebookAccountCapabilitiesWire = {
  platform?: string;
  displayName?: string;
  limits?: {
    reel?: {
      minDurationSeconds?: number;
      maxDurationSeconds?: number;
      minShortSide?: number;
      minLongSide?: number;
    };
    video?: { minDurationSeconds?: number; maxDurationSeconds?: number; maxSizeBytes?: number };
  };
};

const parseCapabilities = (raw: unknown): FacebookAccountCapabilities => {
  const wire = raw as FacebookAccountCapabilitiesWire | null;
  if (wire?.platform !== 'facebook') {
    throw new Error(`facebook page with capabilities for ${String(wire?.platform)}`);
  }
  const { reel, video } = wire.limits ?? {};
  return {
    platform: 'facebook',
    available: true,
    displayName: wire.displayName ?? '',
    limits: {
      reel: {
        minDurationSeconds: reel?.minDurationSeconds ?? 0,
        maxDurationSeconds: reel?.maxDurationSeconds ?? Infinity,
        minShortSide: reel?.minShortSide ?? 0,
        minLongSide: reel?.minLongSide ?? 0,
      },
      video: {
        minDurationSeconds: video?.minDurationSeconds ?? 0,
        maxDurationSeconds: video?.maxDurationSeconds ?? Infinity,
        maxSizeBytes: video?.maxSizeBytes ?? Infinity,
      },
    },
  };
};

/**
 * Reel durations stay in raw seconds: the cap is 90 s, which the duration
 * formatter would round to a minute. An unbounded value reads as the
 * formatter's "∞", so the video half of the same line agrees with it.
 */
const seconds = (value: number): string => (Number.isFinite(value) ? value.toLocaleString() : '∞');

export const describeReelRule = ({ limits: { reel } }: FacebookAccountCapabilities): string =>
  `At least ${reel.minShortSide} by ${reel.minLongSide} pixels, ${seconds(reel.minDurationSeconds)} to ${seconds(reel.maxDurationSeconds)} seconds long.`;

export const describeVideoRule = ({ limits: { video } }: FacebookAccountCapabilities): string =>
  `Up to ${formatDurationToString(video.maxDurationSeconds)} and ${formatBytesToString(video.maxSizeBytes)}.`;

const describeCapabilities = (capabilities: FacebookAccountCapabilities): string => {
  const { reel, video } = capabilities.limits;
  return `Reels ${seconds(reel.minDurationSeconds)} to ${seconds(reel.maxDurationSeconds)} seconds, videos up to ${formatDurationToString(video.maxDurationSeconds)} and ${formatBytesToString(video.maxSizeBytes)}`;
};

export const facebookModule: SocialPlatformModule<
  FacebookPostContent,
  FacebookAccountCapabilities
> = {
  platform: 'facebook',
  label: PLATFORMS.facebook.displayName,
  icon: <img src={PLATFORMS.facebook.icon} alt="" />,
  colors: { bg: 'rgba(24, 119, 242, 0.18)', text: '#8dbcff' },
  accountNoun: 'Page',
  disconnectNote:
    'The Page is forgotten on StarZero’s side only: Facebook keeps StarZero’s permission under your Facebook settings until you remove it there.',
  // FIXME(atlas #382): a stopgap while Meta's App Review of the publishing
  // permissions is pending. The app is in development mode, so only accounts
  // with a role on it can log in; everyone else meets Meta's "Feature
  // unavailable" page. Remove the note once the review passes.
  connectNote:
    'Meta has not reviewed StarZero’s Facebook access yet, so only accounts added as testers on our Meta app can connect.',
  // Meta's dialog refuses to continue with no Page ticked, so this is a missing role or permission, not an empty selection.
  noAccountsNote:
    'Your login shared no Page that StarZero can post to. Connect again and share a Page you have posting rights on.',
  parseContent,
  describeContent,
  defaultContent,
  missingField,
  parseCapabilities,
  describeCapabilities,
  ComposeFields: FacebookComposeFields,
};
